import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Search,
  RotateCcw,
  Database,
  Cloud,
  Terminal,
  Radio,
  Upload,
  GitBranch,
  FileArchive,
  Calculator,
  Sparkles,
  Info,
  X,
  FileCode,
  ShieldAlert,
  Wrench,
  DollarSign,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";

const CRAWL_STEPS = [
  { label: "Fetching sources", tag: "git · zip · archive" },
  { label: "Walking the file tree", tag: "modules · packages" },
  { label: "Detecting language & runtime", tag: "cobol · rpg · java · .net · py" },
  { label: "Discovering data stores", tag: "sql · ddl · pl/sql · dbf" },
  { label: "Sampling traffic contracts", tag: "openapi · wsdl · grpc" },
  { label: "Fingerprinting infra", tag: "docker · terraform · helm · iis" },
  { label: "Building stack-neutral system map", tag: "IR" },
];

const fmtNum = (n) => (Number(n) || 0).toLocaleString();

export default function Scan() {
  const [systems, setSystems] = useState([]);
  const [stats, setStats] = useState(null);
  const [sourceLabel, setSourceLabel] = useState(null);

  const [mode, setMode] = useState("git"); // 'git' | 'zip'
  const [repoUrl, setRepoUrl] = useState("");
  const [pickedFile, setPickedFile] = useState(null);
  const fileInputRef = useRef(null);

  const [preview, setPreview] = useState(null); // { preview_id, files, loc, estimate, provider, source_label, breakdown }
  const [estimating, setEstimating] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [step, setStep] = useState(-1);

  const [providers, setProviders] = useState([]);       // [{id,label}]
  const [savedProviders, setSavedProviders] = useState([]); // [providerId]
  const [aiProvider, setAiProvider] = useState("");    // '' = workspace default / auto

  // Drawer state for the Debt / Vulns / Cost drill-down.
  const [detail, setDetail] = useState({ open: false, system: null, kind: null });
  const openDetail = (system, kind) => setDetail({ open: true, system, kind });

  const load = async () => {
    const [sys, st, specs, keys, act] = await Promise.all([
      api.get("/api/systems"),
      api.get("/api/scan/stats"),
      api.get("/api/byok/providers"),
      api.get("/api/byok"),
      api.get("/api/byok/active"),
    ]);
    setSystems(sys.data);
    setStats(st.data?.stats || null);
    setSourceLabel(st.data?.source_label || null);
    setProviders(specs.data?.providers || []);
    setSavedProviders((keys.data || []).map((k) => k.provider));
    setAiProvider(act.data?.active || "");
  };

  useEffect(() => {
    load();
  }, []);

  const resetPreview = async ({ silent = false } = {}) => {
    if (preview?.preview_id) {
      try { await api.delete(`/api/scan/preview/${preview.preview_id}`); } catch {}
    }
    setPreview(null);
    setConfirmed(false);
    if (!silent) toast.success("Preview cleared");
  };

  const runEstimateGit = async () => {
    const url = repoUrl.trim();
    if (!url) {
      toast.error("Paste a git repo URL first");
      return;
    }
    if (preview) await resetPreview({ silent: true });
    setEstimating(true);
    try {
      const { data } = await api.post("/api/scan/estimate/git", {
        repo_url: url,
        ai_provider: aiProvider || undefined,
      });
      setPreview(data);
      setConfirmed(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setEstimating(false);
    }
  };

  const runEstimateZip = async () => {
    if (!pickedFile) {
      toast.error("Pick a .zip archive first");
      return;
    }
    if (preview) await resetPreview({ silent: true });
    setEstimating(true);
    try {
      const form = new FormData();
      form.append("file", pickedFile);
      const qs = aiProvider ? `?ai_provider=${encodeURIComponent(aiProvider)}` : "";
      const { data } = await api.post(`/api/scan/estimate/upload${qs}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setConfirmed(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setEstimating(false);
    }
  };

  const animateSteps = async (until) => {
    for (let i = 0; i < CRAWL_STEPS.length; i++) {
      if (until.done) break;
      setStep(i);
      await new Promise((r) => setTimeout(r, 380));
    }
  };

  const startScan = async () => {
    if (!preview || !confirmed) return;
    setScanning(true);
    setStep(0);
    const until = { done: false };
    try {
      const req = api.post("/api/scan/confirm", { preview_id: preview.preview_id, confirmed: true });
      await Promise.all([
        animateSteps(until),
        (async () => {
          const { data } = await req;
          until.done = true;
          setSystems(data.systems);
          setStats(data.stats);
          setSourceLabel(preview.source_label);
          setPreview(null);
          setConfirmed(false);
          setRepoUrl("");
          setPickedFile(null);
          toast.success(`Discovered ${data.systems.length} modules`);
        })(),
      ]);
    } catch (e) {
      until.done = true;
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setScanning(false);
      setStep(-1);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset scan? This clears all systems, findings, and roadmap.")) return;
    await api.delete("/api/scan/reset");
    setPreview(null);
    setConfirmed(false);
    await load();
    toast.success("Workspace reset");
  };

  const hasScan = !!(stats && stats.files > 0);
  const canEstimate = mode === "git" ? !!repoUrl.trim() : !!pickedFile;

  return (
    <div className="p-6 md:p-10 space-y-8 relative">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">/ stage · 01</div>
          <h1 className="mt-2 text-3xl md:text-4xl font-medium tracking-tight">
            Auto-crawl the legacy estate.
          </h1>
          <p className="mt-2 text-[color:var(--text-2)] max-w-2xl">
            Zero manual documentation. Point Revora at a git repo or drop a
            zipped codebase — Revora runs a lightweight pre-scan first, gives
            you an accurate token-cost estimate, and only starts the full AI scan
            once you confirm the spend.
          </p>
        </div>
        {hasScan && (
          <button
            data-testid="reset-scan-btn"
            onClick={reset}
            className="inline-flex items-center gap-2 border border-[color:var(--border-strong)] h-11 px-4 hover:border-[color:var(--critical)] hover:text-[color:var(--critical)] transition-colors duration-150 mono text-xs uppercase tracking-widest"
          >
            <RotateCcw size={14} /> Reset
          </button>
        )}
      </div>

      {/* Step 1 — Input */}
      <div className="border border-[color:var(--border)] bg-[color:var(--bg-1)]">
        <div className="border-b border-[color:var(--border)] flex items-center justify-between">
          <div className="flex">
            <ModeTab
              active={mode === "git"}
              onClick={() => { setMode("git"); if (preview) resetPreview({ silent: true }); }}
              icon={<GitBranch size={14} />}
              label="Git Repo"
              testId="scan-mode-git"
            />
            <ModeTab
              active={mode === "zip"}
              onClick={() => { setMode("zip"); if (preview) resetPreview({ silent: true }); }}
              icon={<FileArchive size={14} />}
              label="ZIP Upload"
              testId="scan-mode-zip"
            />
          </div>
          <div className="pr-4 mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)] hidden md:block">
            step 1 · source
          </div>
        </div>
        <div className="p-6 space-y-4">
          {/* Provider selector — enterprise multi-provider */}
          <ProviderSelector
            providers={providers}
            savedProviders={savedProviders}
            value={aiProvider}
            onChange={(v) => { setAiProvider(v); if (preview) resetPreview({ silent: true }); }}
          />
          {mode === "git" ? (
            <input
              data-testid="repo-url-input"
              type="url"
              value={repoUrl}
              onChange={(e) => { setRepoUrl(e.target.value); if (preview) resetPreview({ silent: true }); }}
              placeholder="https://github.com/org/legacy-app.git"
              className="w-full h-11 bg-[color:var(--bg-base)] border border-[color:var(--border)] px-3 mono text-sm text-white placeholder:text-[color:var(--text-3)] focus:border-[color:var(--accent)] focus:outline-none focus:ring-1 focus:ring-[color:var(--accent)] transition-colors duration-150"
              onKeyDown={(e) => e.key === "Enter" && canEstimate && !estimating && runEstimateGit()}
            />
          ) : (
            <ZipDrop
              onFile={(f) => { setPickedFile(f); if (preview) resetPreview({ silent: true }); }}
              file={pickedFile}
              inputRef={fileInputRef}
              disabled={estimating || scanning}
            />
          )}
          <div className="flex flex-wrap items-center gap-3">
            <button
              data-testid="run-prescan-btn"
              onClick={mode === "git" ? runEstimateGit : runEstimateZip}
              disabled={!canEstimate || estimating || scanning}
              className="inline-flex items-center gap-2 bg-[color:var(--accent)] text-black h-11 px-5 hover:bg-[color:var(--accent-hover)] transition-colors duration-150 mono text-xs uppercase tracking-widest disabled:opacity-40"
            >
              <Calculator size={14} />
              {estimating ? "Analyzing…" : "Run Pre-Scan Analysis"}
            </button>
            <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)]">
              {mode === "git"
                ? "public repos · shallow clone · https or git@ url"
                : ".zip · up to 250MB · zip-slip protected"}
            </div>
          </div>
        </div>
      </div>

      {/* Step 2 — Estimate */}
      {preview && (
        <div
          data-testid="prescan-result"
          className="border border-[color:var(--accent)]/40 bg-[color:var(--bg-1)] relative"
        >
          <div className="absolute inset-0 grid-bg opacity-15 pointer-events-none" />
          <div className="relative">
            <div className="border-b border-[color:var(--border)] px-6 py-3 flex items-center justify-between">
              <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--accent)] flex items-center gap-2">
                <Calculator size={12} /> step 2 · cost estimate
              </div>
              <button
                data-testid="discard-preview-btn"
                onClick={() => resetPreview()}
                className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)] hover:text-[color:var(--critical)] inline-flex items-center gap-1"
                title="Discard preview"
              >
                <X size={12} /> discard
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div className="mono text-xs text-[color:var(--text-3)] truncate">
                source · <span className="text-[color:var(--text-2)]">{preview.source_label}</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <EstCard label="Total Files" value={fmtNum(preview.files)} testId="est-files" />
                <EstCard label="Total LOC" value={fmtNum(preview.loc)} testId="est-loc" />
                <EstCard
                  label="Estimated Tokens"
                  value={fmtNum(preview.estimate?.total_tokens)}
                  hint={`${fmtNum(preview.estimate?.input_tokens)} input · ${fmtNum(preview.estimate?.response_tokens)} response`}
                  accent
                  testId="est-tokens"
                />
              </div>

              {preview.breakdown && preview.breakdown.length > 0 && (
                <div>
                  <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)] mb-2">
                    top languages by LOC
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {preview.breakdown.map((b) => (
                      <span
                        key={b.ext}
                        className="mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-[color:var(--border-strong)] text-[color:var(--text-2)]"
                      >
                        {b.ext} · {fmtNum(b.loc)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <label
                htmlFor="scan-confirm-checkbox"
                className="flex items-start gap-3 cursor-pointer border border-[color:var(--border)] bg-[color:var(--bg-2)] p-4 hover:border-[color:var(--accent)]/60 transition-colors duration-150"
              >
                <input
                  id="scan-confirm-checkbox"
                  data-testid="scan-confirm-checkbox"
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="mt-1 accent-[color:var(--accent)] w-4 h-4"
                />
                <span className="text-sm text-[color:var(--text-2)]">
                  I confirm that I have sufficient balance in my{" "}
                  <span className="text-white font-medium">{preview.provider}</span>{" "}
                  account for approximately{" "}
                  <span className="text-[color:var(--accent)] font-medium mono">
                    {fmtNum(preview.estimate?.total_tokens)}
                  </span>{" "}
                  tokens.
                </span>
              </label>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  data-testid="start-ai-scan-btn"
                  onClick={startScan}
                  disabled={!confirmed || scanning}
                  className="inline-flex items-center gap-2 bg-[color:var(--accent)] text-black h-11 px-5 hover:bg-[color:var(--accent-hover)] transition-colors duration-150 mono text-xs uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Sparkles size={14} />
                  {scanning ? "Scanning…" : "Start AI Scan"}
                </button>
                <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)] flex items-center gap-1.5">
                  <Info size={11} />
                  Estimation is based on a factor of {preview.estimate?.factor ?? 20} tokens per line;
                  actual usage may vary based on model reasoning.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Source cards — numbers derived from the actual scan */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SourceCard
          icon={<Terminal size={14} />}
          label="Modules"
          v={stats?.modules ?? "—"}
          hint={stats ? `${(stats.files ?? 0).toLocaleString()} files` : "run a scan"}
          testId="source-modules"
        />
        <SourceCard
          icon={<Database size={14} />}
          label="Data stores"
          v={stats?.data_stores ?? "—"}
          hint={
            stats && Object.keys(stats.data_breakdown || {}).length
              ? Object.keys(stats.data_breakdown).slice(0, 2).join(" · ").toLowerCase()
              : "sql · ddl · pl/sql"
          }
          testId="source-data"
        />
        <SourceCard
          icon={<Cloud size={14} />}
          label="Runtimes"
          v={stats?.runtimes ?? "—"}
          hint={
            stats && Object.keys(stats.infra_breakdown || {}).length
              ? Object.keys(stats.infra_breakdown).slice(0, 2).join(" · ").toLowerCase()
              : "docker · tf · helm"
          }
          testId="source-runtimes"
        />
        <SourceCard
          icon={<Radio size={14} />}
          label="Traffic taps"
          v={stats?.traffic_taps ?? "—"}
          hint={
            stats && Object.keys(stats.traffic_breakdown || {}).length
              ? Object.keys(stats.traffic_breakdown).slice(0, 2).join(" · ").toLowerCase()
              : "openapi · grpc · wsdl"
          }
          testId="source-traffic"
        />
      </div>

      {/* Scan progress overlay */}
      {scanning && (
        <div
          data-testid="scan-overlay"
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
        >
          <div className="w-full max-w-xl border border-[color:var(--accent)]/40 bg-[color:var(--bg-1)] p-6 relative overflow-hidden">
            <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
            <div className="relative">
              <div className="eyebrow text-[color:var(--accent)]">/ scan in progress</div>
              <div className="mt-2 text-xl font-medium">
                Building stack-neutral system map…
              </div>
              <div className="mt-6 space-y-2 mono text-xs">
                {CRAWL_STEPS.map((s, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span
                      className={`w-1.5 h-1.5 shrink-0 ${
                        i < step
                          ? "bg-[color:var(--success)]"
                          : i === step
                          ? "bg-[color:var(--accent)] pulse-dot rounded-full"
                          : "bg-[color:var(--border-strong)]"
                      }`}
                    />
                    <span className={i <= step ? "text-white" : "text-[color:var(--text-3)]"}>
                      {s.label}
                    </span>
                    <span className="ml-auto text-[color:var(--text-3)] uppercase tracking-widest text-[10px]">
                      {s.tag}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Systems inventory */}
      <div>
        <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
          <div className="eyebrow">/ discovered systems ({systems.length})</div>
          {sourceLabel && (
            <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)] truncate max-w-full">
              source · <span className="text-[color:var(--text-2)]">{sourceLabel}</span>
            </div>
          )}
        </div>

        {systems.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {systems.map((s) => (
              <SystemCard key={s.id} s={s} onOpenDetail={openDetail} />
            ))}
          </div>
        )}
      </div>

      {/* Right-side drill-down drawer for Debt / Vulns / Cost */}
      <SystemDetailsDrawer
        open={detail.open}
        system={detail.system}
        kind={detail.kind}
        onClose={() => setDetail((d) => ({ ...d, open: false }))}
      />
    </div>
  );
}

function ProviderSelector({ providers, savedProviders, value, onChange }) {
  const savedSet = new Set(savedProviders);
  const label =
    providers.find((p) => p.id === value)?.label ||
    (savedProviders.length ? `Auto — ${savedProviders[0]}` : "Auto — Shared Emergent key");
  return (
    <div
      data-testid="scan-provider-selector"
      className="flex items-center justify-between gap-3 flex-wrap border border-[color:var(--border)] bg-[color:var(--bg-2)] px-3 py-2"
    >
      <label
        htmlFor="scan-ai-provider"
        className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)] shrink-0"
      >
        Select AI Provider
      </label>
      <div className="flex items-center gap-2 flex-1 min-w-[220px] justify-end">
        <select
          id="scan-ai-provider"
          data-testid="scan-ai-provider"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 min-w-[240px] bg-[color:var(--bg-base)] border border-[color:var(--border-strong)] px-2 mono text-xs text-white focus:border-[color:var(--accent)] focus:outline-none focus:ring-1 focus:ring-[color:var(--accent)]"
        >
          <option value="">Auto — use workspace default</option>
          {providers.map((p) => {
            const saved = savedSet.has(p.id);
            return (
              <option key={p.id} value={p.id} disabled={!saved}>
                {p.label}
                {saved ? "" : " (not configured)"}
              </option>
            );
          })}
        </select>
        <span
          className={`mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${
            value && savedSet.has(value)
              ? "border-[color:var(--success)]/40 text-[color:var(--success)]"
              : savedProviders.length
              ? "border-[color:var(--border-strong)] text-[color:var(--text-3)]"
              : "border-[color:var(--warning)]/40 text-[color:var(--warning)]"
          }`}
          title={label}
        >
          {value && savedSet.has(value) ? "ready" : savedProviders.length ? "auto" : "shared key"}
        </span>
      </div>
    </div>
  );
}


function ModeTab({ active, onClick, icon, label, testId }) {  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`flex items-center gap-2 px-5 py-3 mono text-xs uppercase tracking-widest border-b-2 transition-colors duration-150 ${
        active
          ? "border-[color:var(--accent)] text-white bg-[color:var(--bg-2)]"
          : "border-transparent text-[color:var(--text-2)] hover:text-white"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function ZipDrop({ onFile, file, inputRef, disabled }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      data-testid="zip-drop"
      onDragOver={(e) => { e.preventDefault(); setHover(true); }}
      onDragLeave={() => setHover(false)}
      onDrop={(e) => {
        e.preventDefault();
        setHover(false);
        const f = e.dataTransfer?.files?.[0];
        if (f) onFile(f);
      }}
      onClick={() => inputRef.current?.click()}
      className={`border-2 border-dashed p-8 flex flex-col items-center justify-center gap-2 cursor-pointer transition-colors duration-150 ${
        hover
          ? "border-[color:var(--accent)] bg-[color:var(--accent)]/5"
          : "border-[color:var(--border-strong)] hover:border-white/50"
      } ${disabled ? "pointer-events-none opacity-50" : ""}`}
    >
      <Upload size={20} className="text-[color:var(--accent)]" />
      <div className="text-sm font-medium">
        {file ? file.name : "Drop a .zip here"}
      </div>
      <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)]">
        {file ? `${(file.size / 1024 / 1024).toFixed(1)} MB · click to replace` : "or click to browse"}
      </div>
      <input
        ref={inputRef}
        data-testid="zip-file-input"
        type="file"
        accept=".zip,application/zip"
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0])}
      />
    </div>
  );
}

function EstCard({ label, value, hint, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className={`border p-4 ${
        accent
          ? "border-[color:var(--accent)]/40 bg-[color:var(--accent)]/5"
          : "border-[color:var(--border)] bg-[color:var(--bg-2)]"
      }`}
    >
      <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)]">
        {label}
      </div>
      <div className={`mt-2 mono text-2xl ${accent ? "text-[color:var(--accent)]" : "text-white"}`}>
        {value}
      </div>
      {hint && (
        <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)] mt-1">
          {hint}
        </div>
      )}
    </div>
  );
}

function SourceCard({ icon, label, v, hint, testId }) {
  return (
    <div
      data-testid={testId}
      className="border border-[color:var(--border)] bg-[color:var(--bg-1)] p-4"
    >
      <div className="flex items-center justify-between text-[color:var(--text-2)]">
        <span className="eyebrow">{label}</span>
        {icon}
      </div>
      <div className="mt-2 mono text-2xl">{v}</div>
      <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)]">
        {hint}
      </div>
    </div>
  );
}

function SystemCard({ s, onOpenDetail }) {
  const critColor = {
    Critical: "text-[color:var(--critical)] border-[color:var(--critical)]/40",
    High: "text-[color:var(--warning)] border-[color:var(--warning)]/40",
    Medium: "text-[color:var(--info)] border-[color:var(--info)]/40",
    Low: "text-[color:var(--success)] border-[color:var(--success)]/40",
  }[s.crit] || "text-white border-white/20";

  const debtCount = (s.debt_details || []).length;
  const vulnCount = (s.vuln_details || []).length;
  const costCount = (s.cost_breakdown || []).length;

  return (
    <div
      data-testid={`system-card-${s.id}`}
      className="border border-[color:var(--border)] bg-[color:var(--bg-1)] p-5 hover:border-[color:var(--accent)]/40 transition-colors duration-150"
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)]">
            {s.domain}
          </div>
          <div className="mt-1 text-lg font-medium tracking-tight truncate">
            {s.name}
          </div>
        </div>
        <span className={`mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border shrink-0 ${critColor}`}>
          {s.crit}
        </span>
      </div>
      <div className="mt-3 mono text-xs text-[color:var(--text-2)] truncate">{s.tech}</div>
      {s.description && (
        <div
          className="mt-2 mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)] leading-snug"
          title={s.description}
        >
          {s.description}
        </div>
      )}
      <div className="mt-4 grid grid-cols-3 gap-2 mono">
        <StatButton
          label="Debt"
          v={s.tech_debt > 0 ? s.tech_debt : "—"}
          badge={debtCount || null}
          onClick={() => onOpenDetail(s, "debt")}
          testId={`stat-debt-${s.id}`}
          disabled={!s.tech_debt}
        />
        <StatButton
          label="Vulns"
          v={s.vulns_total}
          badge={vulnCount || null}
          onClick={() => onOpenDetail(s, "vulns")}
          testId={`stat-vulns-${s.id}`}
          disabled={!s.vulns_total && vulnCount === 0}
        />
        <StatButton
          label={s.cost > 0 ? "Cost" : "LOC"}
          v={
            s.cost > 0
              ? s.cost >= 1_000_000
                ? `$${(s.cost / 1_000_000).toFixed(1)}M`
                : `$${(s.cost / 1000).toFixed(0)}K`
              : (s.loc || 0).toLocaleString()
          }
          badge={costCount || null}
          onClick={() => onOpenDetail(s, "cost")}
          testId={`stat-cost-${s.id}`}
          disabled={!s.cost && !costCount}
        />
      </div>
    </div>
  );
}

function StatButton({ label, v, badge, onClick, testId, disabled }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      className="text-left border-t border-[color:var(--border)] pt-2 -mx-1 px-1 group hover:bg-[color:var(--accent)]/5 hover:border-[color:var(--accent)]/60 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:border-[color:var(--border)]"
      title={disabled ? `${label} — no detail rows` : `Inspect ${label.toLowerCase()} details`}
    >
      <div className="flex items-center justify-between gap-1">
        <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-3)]">
          {label}
        </div>
        {badge != null && (
          <span className="mono text-[9px] uppercase tracking-widest px-1 border border-[color:var(--accent)]/40 text-[color:var(--accent)] group-hover:bg-[color:var(--accent)]/10">
            {badge}
          </span>
        )}
      </div>
      <div className="text-white text-sm mt-0.5 group-hover:text-[color:var(--accent)] transition-colors duration-150">
        {v}
      </div>
    </button>
  );
}

const SEV_TONE = {
  critical: "text-[color:var(--critical)] border-[color:var(--critical)]/40 bg-[color:var(--critical)]/10",
  high: "text-[color:var(--warning)] border-[color:var(--warning)]/40 bg-[color:var(--warning)]/10",
  medium: "text-[color:var(--info)] border-[color:var(--info)]/40 bg-[color:var(--info)]/10",
  low: "text-[color:var(--text-2)] border-[color:var(--border-strong)] bg-[color:var(--bg-2)]",
};

const KIND_META = {
  debt:  { label: "Debt",  title: "Legacy Debt Signals", icon: Wrench,       accent: "var(--warning)"  },
  vulns: { label: "Vulns", title: "Security Findings",   icon: ShieldAlert,  accent: "var(--critical)" },
  cost:  { label: "Cost",  title: "Cost Breakdown",      icon: DollarSign,   accent: "var(--accent)"   },
};

function fmtUSD(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toLocaleString()}`;
}

function SystemDetailsDrawer({ open, system, kind, onClose }) {
  const meta = KIND_META[kind];
  const IconEl = meta?.icon;

  const rows =
    kind === "vulns"
      ? system?.vuln_details || []
      : kind === "debt"
      ? system?.debt_details || []
      : system?.cost_breakdown || [];

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent
        side="right"
        data-testid="system-detail-drawer"
        className="w-full sm:max-w-lg md:max-w-xl bg-[color:var(--bg-1)] border-l border-[color:var(--border-strong)] text-white p-0 overflow-hidden flex flex-col"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[color:var(--border)] text-left">
          <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)] flex items-center gap-2">
            {IconEl && <IconEl size={12} style={{ color: meta.accent }} />}
            {system?.domain || "system"} · {system?.tech || ""}
          </div>
          <SheetTitle
            className="text-lg font-medium tracking-tight text-white"
            data-testid="detail-drawer-title"
          >
            {system?.name} — {meta?.title || "Details"}
          </SheetTitle>
          <SheetDescription className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)]">
            {rows.length} row{rows.length === 1 ? "" : "s"}
            {kind === "cost" && system ? ` · total ${fmtUSD(system.cost)}` : ""}
            {kind === "vulns" && system ? ` · weighted score ${system.vulns_total}` : ""}
            {kind === "debt" && system ? ` · debt score ${system.tech_debt}` : ""}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 mono text-xs">
          {rows.length === 0 ? (
            <DrawerEmpty kind={kind} system={system} />
          ) : kind === "cost" ? (
            <CostList rows={rows} totalCost={system?.cost || 0} />
          ) : (
            <IssueList rows={rows} />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function DrawerEmpty({ kind, system }) {
  const msg =
    kind === "debt"
      ? `No specific legacy hotspots detected. Debt score ${system?.tech_debt ?? 0} is derived from overall tech-weight composition.`
      : kind === "vulns"
      ? "No vulnerability patterns matched during static scan."
      : "No cost-relevant files sampled.";
  return (
    <div className="border border-dashed border-[color:var(--border-strong)] p-6 text-[color:var(--text-3)]">
      {msg}
    </div>
  );
}

function IssueList({ rows }) {
  return (
    <div className="divide-y divide-[color:var(--border)]">
      {rows.map((r, i) => (
        <div key={i} data-testid={`detail-row-${i}`} className="py-3 first:pt-0">
          <div className="flex items-start gap-2 flex-wrap">
            <span
              className={`inline-flex px-1.5 py-0.5 border text-[9px] uppercase tracking-widest ${SEV_TONE[r.severity] || SEV_TONE.low}`}
            >
              {r.severity || "low"}
            </span>
            <div className="text-white text-sm flex-1 min-w-0 break-words">
              {r.title}
              {r.count && r.count > 1 ? (
                <span className="ml-2 text-[color:var(--text-3)]">×{r.count}</span>
              ) : null}
            </div>
          </div>
          {r.description && (
            <div className="mt-1 text-[color:var(--text-2)] leading-snug">
              {r.description}
            </div>
          )}
          <div className="mt-2 flex items-center gap-1.5 text-[color:var(--text-2)] break-all">
            <FileCode size={11} className="shrink-0 text-[color:var(--accent)]" />
            <span data-testid={`detail-path-${i}`}>{r.file_path || "—"}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function CostList({ rows, totalCost }) {
  const total = totalCost || rows.reduce((sum, r) => sum + (r.cost || 0), 0);
  return (
    <div className="space-y-2">
      {rows.map((r, i) => {
        const pct = total > 0 ? Math.min(100, Math.round(((r.cost || 0) / total) * 100)) : 0;
        return (
          <div
            key={i}
            data-testid={`detail-row-${i}`}
            className="border border-[color:var(--border)] bg-[color:var(--bg-2)] p-3"
          >
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 min-w-0">
                <span className="px-1.5 py-0.5 border border-[color:var(--border-strong)] text-[10px] uppercase tracking-widest text-[color:var(--text-2)]">
                  {r.ext}
                </span>
                <span className="text-white text-sm truncate">{r.tech}</span>
              </div>
              <div className="mono text-sm text-[color:var(--accent)]">
                {fmtUSD(r.cost)}
              </div>
            </div>
            <div className="mt-2 text-[10px] uppercase tracking-widest text-[color:var(--text-3)] flex items-center justify-between">
              <span>
                {(r.loc || 0).toLocaleString()} loc · {r.files} file{r.files === 1 ? "" : "s"}
              </span>
              <span>{pct}% of module</span>
            </div>
            <div className="mt-1 h-1 bg-[color:var(--bg-base)] overflow-hidden">
              <div
                className="h-full bg-[color:var(--accent)]"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Stat({ label, v }) {
  return (
    <div className="border-t border-[color:var(--border)] pt-2">
      <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-3)]">
        {label}
      </div>
      <div className="text-white text-sm mt-0.5">{v}</div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="border border-dashed border-[color:var(--border-strong)] bg-[color:var(--bg-1)] p-10 text-center">
      <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--accent)]">
        empty inventory
      </div>
      <div className="mt-2 text-lg text-[color:var(--text-2)]">
        Nothing scanned yet.
      </div>
      <div className="mt-2 mono text-xs text-[color:var(--text-3)]">
        Paste a git URL or drop a .zip above · run Pre-Scan Analysis · confirm cost · start AI scan.
      </div>
    </div>
  );
}
